import React, { useMemo } from 'react';

import {
  DirectoryEntry,
  FileClickProps,
  FileEntry,
  TreeEntry,
} from 'src/components/file-tree';
import { flattenDirectories } from 'src/components/file-tree/src/flatten-directories';

import { FileListDirectory } from './file-list-directory';
import { FileListFile } from './file-list-file';
import { isFile, flattenFiles } from './flatten-files';
import * as styles from './styled';

export type FileListProps = {
  fileTree: TreeEntry[] | null;
  expandAll?: boolean;
  onClick?: (event: React.MouseEvent, props: FileClickProps) => void;
};

export const FileList = ({
  fileTree,
  expandAll = false,
  onClick = () => {},
}: FileListProps) => {
  const flattenedDirs = useMemo(() => {
    return flattenFiles(fileTree?.map(flattenDirectories) || []);
  }, [fileTree]);

  const renderFile = (file: FileEntry) => {
    const {
      name,
      fileDiffStatus,
      href,
      isConflicted,
      isHidden,
      comments,
      linesAdded,
      linesRemoved,
    } = file;
    return (
      <FileListFile
        key={name}
        name={name}
        onClick={onClick}
        href={href}
        fileDiffStatus={fileDiffStatus}
        isConflicted={isConflicted}
        isHidden={isHidden}
        comments={comments}
        linesAdded={linesAdded}
        linesRemoved={linesRemoved}
      />
    );
  };

  const renderDirectory = (directory: DirectoryEntry, index: number) => {
    const { name, contents } = directory;
    const fileCount = contents.reduce(
      (count, entry) => count + (isFile(entry) ? 1 : 0),
      0
    );

    // contents will only be files due to flattening
    return (
      <FileListDirectory
        name={name}
        fileCount={fileCount}
        defaultCollapsed={!expandAll}
        key={name}
        isLastDir={index === flattenedDirs.length - 1}
      >
        {contents.map((entry: FileEntry, idx: number) => (
          <li key={idx}>{renderFile(entry)}</li>
        ))}
      </FileListDirectory>
    );
  };

  return (
    <styles.NonStyledList role="list">
      {flattenedDirs.map(renderDirectory)}
    </styles.NonStyledList>
  );
};
